import type { deUi } from './de.ui';

/** English texts for the interface shell. See de.ui.ts for the reference. */
export const enUi: Record<keyof typeof deUi, string> = {
  'nav.dashboard': 'Dashboard',
  'nav.statistics': 'Statistics',
  'nav.balance': 'Credit',
  'nav.settings': 'Settings',
  'nav.language': 'Language',
  'nav.main': 'Main navigation',

  'action.close': 'Close',
  'action.confirm': 'Confirm',
  'action.change': 'Change',
  'action.remove': 'Remove',
  'action.edit': 'Edit',

  'help.about': 'Show explanation',
  'help.aboutLabel': 'Explanation of {label}',

  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Hello, {name}',
  'dashboard.balance': 'Credit',
  'dashboard.minutesToday': 'Talk minutes today',
  'dashboard.activeCallers': 'Callers online',
  'dashboard.runningCalls': 'Calls in progress',
  'dashboard.campaigns': 'Active campaigns',
  'dashboard.resultsToday': 'Deals closed today',
  'dashboard.noData': 'No numbers yet',
  'dashboard.help.balance': 'Your current prepaid credit. If it drops below one talk minute, no new calls can start.',
  'dashboard.help.minutesToday': 'Total talk time actually connected today. Ringing and waiting are not counted.',
  'dashboard.help.activeCallers': 'Callers who are signed in right now and could take calls.',
  'dashboard.help.runningCalls': 'Calls that are running at this very moment.',
  'dashboard.help.campaigns': 'Campaigns that are currently started. Stopped and archived ones are not counted.',
  'dashboard.help.resultsToday': 'Calls closed as successful today.',
  'dashboard.quickStart': 'Getting started',
  'dashboard.quickStartText': 'Create a campaign, import your records and release it to your callers.',
  'dashboard.openCampaigns': 'Go to campaigns',

  'soon.title': 'This area is not available yet',
  'soon.statistics': 'Reports on calls, callers and campaigns are coming in one of the next steps. Your calls are already being logged in full.',
  'soon.campaignStatistics': 'The report for this campaign is coming in one of the next steps.',
  'soon.campaignRecordings': 'Call recordings are coming in one of the next steps.',
  'soon.hint': 'You can carry on here later - nothing is lost.',

  'balance.title': 'Credit',
  'balance.current': 'Current credit',
  'balance.pricePerMinute': 'Price per talk minute',
  'balance.help.pricePerMinute': 'Only connected talk time is billed, rounded up to full minutes per call.',
  'balance.topUp': 'Top up balance',
  'balance.topUpText': 'Topping up works with crypto payments. Card payment is not planned yet.',
  'balance.amount': 'Amount in euros',
  'balance.minimum': 'Minimum amount',
  'balance.minimumHint': 'At least {amount} per top-up.',
  'balance.payWithCrypto': 'Top up with crypto',
  'balance.history': 'Payment history',
  'balance.historyEmpty': 'Your top-ups and all other entries will appear here.',
  'balance.date': 'Date',
  'balance.amountColumn': 'Amount',
  'balance.type': 'Kind',
  'balance.balanceAfter': 'Balance afterwards',
  'balance.note': 'Note',

  // --- Kinds of entry -----------------------------------------------------
  'balance.type.topup_nowpayments': 'Balance top-up',
  'balance.type.start_credit': 'Starting balance',
  'balance.type.manual_credit': 'Credit',
  'balance.type.manual_debit': 'Debit',
  'balance.type.phone_number_order': 'Phone number order',
  'balance.type.phone_number_refund': 'Phone number order refunded',
  'balance.type.hosted_sip_monthly_fee': 'Monthly SIP base fee',
  'balance.type.dialer_call': 'Call charges',
  'balance.type.hosted_sip_call': 'Call charges via SIP line',

  // --- Balance error messages ---------------------------------------------
  'error.billing.below_minimum': 'Please top up at least {minTopUpAmount}.',
  'error.billing.amount_invalid': 'Please enter an amount greater than 0.',
  'error.billing.note_too_long': 'The note is too long. Please shorten it.',
  'error.billing.debit_exceeds_balance': 'The balance is not sufficient. {available} available.',
  'error.billing.already_booked': 'This entry has already been made.',
  'error.billing.provider_unavailable': 'Top-ups are currently unavailable. Please contact support.',

  'callers.role': 'Role',
  'callers.roleAdmin': 'Administrator',
  'callers.roleCaller': 'Caller',
  'callers.roleOwner': 'Account owner',
  'callers.state': 'Access',
  'callers.stateActive': 'Active',
  'callers.stateInactive': 'Blocked',
  'callers.createTitle': 'Create a new caller',
  'callers.created': 'The account has been created.',
  'callers.help.username': 'The caller signs in with this name. It has to be unique across the whole platform.',
  'callers.help.admin': 'Administrators may create campaigns, manage records and set up further accounts.',
  'callers.setPasswordTitle': 'Set a new password',
  'callers.setPasswordText': 'The caller can then only sign in with the new password. An open session ends.',
  'callers.setPasswordAction': 'Set password',
  'callers.deactivateTitle': 'Block access',
  'callers.deactivateText': 'This account can no longer sign in. A running session ends immediately. You can unblock it at any time.',
  'callers.deactivateAction': 'Block access',
  'callers.makeAdminTitle': 'Grant administrator rights',
  'callers.makeAdminText': 'This account may then manage campaigns, records and further accounts.',
  'callers.revokeAdminTitle': 'Revoke administrator rights',
  'callers.revokeAdminText': 'This account will only be able to make calls afterwards.',

  'dialog.campaignArchive': 'Archive campaign',
  'dialog.campaignArchiveText': 'The campaign disappears from the normal overview. Records and call history are kept, and you can bring it back at any time.',
  'dialog.campaignDelete': 'Delete campaign',
  'dialog.campaignDeleteText': 'The campaign and all of its records are deleted for good. This cannot be undone.',
  'dialog.contactDelete': 'Delete record',
  'dialog.contactDeleteText': 'The record is deleted for good. If you might still need it, archiving is the better choice.',
  'dialog.modeChange': 'Change how the campaign works',
  'dialog.accountDelete': 'Delete account',
  'dialog.leaveCalling': 'Leave the calling area?',
  'dialog.leaveCallingText': 'The dialler stops and you can no longer take calls.',
  'dialog.leaveCallingAction': 'Leave',

  'campaign.section.general': 'General',
  'campaign.section.generalHint': 'Name, description and the country of the phone numbers.',
  'campaign.section.callers': 'Callers',
  'campaign.section.callersHint': 'How records are shared out among the callers.',
  'campaign.section.dialer': 'Dialler',
  'campaign.section.dialerHint': 'How many calls are set up at once and how often a number is tried again.',
  'campaign.section.telephony': 'Telephony',
  'campaign.section.telephonyHint': 'Your telephony provider account for this campaign.',
  'campaign.section.inbound': 'Incoming calls',
  'campaign.section.inboundHint': 'Whether customers can call back in this campaign.',
  'campaign.section.queue': 'Waiting queue',
  'campaign.section.queueHint': 'What happens when no caller is free.',
  'campaign.section.recording': 'Recordings',
  'campaign.section.recordingHint': 'Whether calls are recorded.',
  'campaign.section.data': 'Custom fields',
  'campaign.section.dataHint': 'Extra details you want to keep for each record.',
  'campaign.section.advanced': 'Advanced settings',

  'campaign.help.mode': 'In a shared pool all callers work through the same list. With fixed assignment each record belongs to one caller, and the others do not see it.',
  'campaign.help.country': 'Needed to complete phone numbers without a country code correctly - for example 0176… to +49176…',
  'campaign.help.parallelCalls': 'How many numbers are dialled at the same time while a caller is free. Higher values find someone faster but raise the risk that a customer has to wait.',
  'campaign.help.parallelMode': 'Does the number apply per caller or to the whole campaign together?',
  'campaign.help.retry': 'How often a number is tried again within 24 hours when nobody picks up.',
  'campaign.help.mailbox': 'Detects answering machines and ends those calls so your callers do not listen to a recorded message.',
  'campaign.help.inbound': 'Lets customers call back in this campaign and reach a free caller.',
  'campaign.help.queue': 'Holds callers briefly in a queue when nobody is free.',
  'campaign.help.queueWait': 'After this time the call ends if nobody has become free.',
  'campaign.help.recording': 'Sets whether calls in this campaign are recorded by default.',
  'campaign.help.fields': 'Extra details per record, such as town or contract number. They appear while calling and in the CSV import.',
  'campaign.help.required': 'This field has to be filled in before a call can be completed.',
  'campaign.help.sip': 'The account details of your telephony provider. Without them no calls can be made in this campaign.',

  'campaign.settings.sip': 'Telephony provider',

  // --- Telephony provider of a campaign -----------------------------------
  'campaign.sip.provider': 'Provider',
  'campaign.sip.providerHint': 'A note for you only - e.g. TENIOS, dus.net or sipgate.',
  'campaign.sip.host': 'SIP server',
  'campaign.sip.hostHint': 'The address your provider gives you - without a leading “sip:”.',
  'campaign.sip.username': 'User name',
  'campaign.sip.password': 'Password',
  'campaign.sip.passwordStored': 'A password is stored. Leave empty to keep it.',
  'campaign.sip.registration': 'Provider requires sign-in',
  'campaign.sip.registrationHelp':
    'Most providers do. Your credentials usually call it “registration” or “REGISTER”.',
  'campaign.sip.advancedShow': 'Show advanced settings',
  'campaign.sip.advancedHide': 'Hide advanced settings',
  'campaign.sip.authId': 'Sign-in ID',
  'campaign.sip.authIdHint': 'Only fill in if it differs from the user name.',
  'campaign.sip.realm': 'Realm',
  'campaign.sip.fromDomain': 'Sender domain',
  'campaign.sip.fromDomainHint': 'Only fill in if your provider expects a different one than the server.',
  'campaign.sip.port': 'Port',
  'campaign.sip.portHint': 'Leave empty for the usual port 5060.',
  'campaign.sip.transport': 'Transport',
  'campaign.sip.transportDefault': 'Default',
  'campaign.sip.optional': 'Optional.',
  'campaign.sip.expiration': 'Sign-in valid for (seconds)',
  'campaign.sip.expirationHint': 'Leave empty for the default.',
  'campaign.sip.retryInterval': 'Retry interval (seconds)',
  'campaign.sip.retryIntervalHint': 'Leave empty for the default.',
  'campaign.sip.saved': 'The credentials have been saved.',
  'campaign.sip.test': 'Test connection',
  'campaign.sip.testing': 'Testing …',
  'campaign.sip.test.ok': 'Telephony is ready.',
  'campaign.sip.test.registered': 'Connected - signing in with the provider succeeded.',
  'campaign.sip.test.registration_failed':
    'Signing in with the provider failed. Please check the server and the credentials.',
  'campaign.sip.test.rejected': 'The provider rejected the credentials.',
  'campaign.sip.test.unreachable': 'The provider server cannot be reached.',
  'campaign.sip.test.timeout': 'The provider did not answer. Please try again later.',
  'campaign.sip.test.busy': 'The connection was checked just now. Please wait a moment and try again.',
  'error.sip.password_required': 'Please enter the telephony provider password.',
  'error.sip.incomplete': 'Please enter the server and the user name.',
};
